import { OfferEligibility, type OfferKind } from "@townsy/contracts";
import { isApiError } from "../api/client";
import { isRewardsUnavailable } from "../api/rewards";
import { formatCoins } from "../lib/wallet";
import { DEFAULT_DAILY_CAP_COINS, eligibilityCopy } from "./copy";

/**
 * Every refusal the sponsored-task routes can return, in plain language, with what the UI does next
 * (docs/06 section 10: never a bare "Something went wrong" when the server has a specific reason). The
 * client branches on `code` and on the server's structured `details`, never on message text. Coins
 * are only ever described as awarded when the server's claim response says so.
 */

export type RewardStage = "load" | "start" | "claim" | "refresh" | "cancel";

export type RewardFailureAction =
  /** Resend the same request (same Idempotency-Key): network, 5xx, rate limit. */
  | "retry"
  /** Re-read the attempt from the server and show its real state (expired, too early, conflict). */
  | "reload_attempt"
  /** Stay on the quiz: the server counted a wrong answer and tries remain. */
  | "answer_again"
  /** Nothing to retry (cap, budget, not eligible). */
  | "close"
  | "sign_in"
  | "verify_email"
  /** Another attempt is active: offer to show it (details.attemptId). */
  | "show_active";

export interface RewardFailure {
  code: string;
  message: string;
  action: RewardFailureAction;
  /** Quiz: tries left after a wrong answer (server count). */
  triesLeft?: number | null;
  /** Website task: the server's earliest claim time. */
  claimableAt?: string | null;
  /** ATTEMPT_IN_PROGRESS: the active attempt. */
  attemptId?: string | null;
  /** OFFER_NOT_ELIGIBLE: the server's eligibility reason. */
  eligibility?: OfferEligibility | null;
}

export const REWARDS_UNAVAILABLE_MESSAGE = "Sponsored tasks are not available on this server yet.";
export const NETWORK_MESSAGE = "Couldn't reach Townsy. Check your connection and try again: a repeated request never awards or charges twice.";

function detailsOf(err: { details?: unknown }): Record<string, unknown> {
  return err.details && typeof err.details === "object" && !Array.isArray(err.details) ? (err.details as Record<string, unknown>) : {};
}

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

export function describeRewardError(err: unknown, ctx: { stage: RewardStage; kind?: OfferKind | null; dailyCapCoins?: number | null } = { stage: "start" }): RewardFailure {
  if (!isApiError(err)) return { code: "NETWORK", message: NETWORK_MESSAGE, action: "retry" };
  if (isRewardsUnavailable(err)) return { code: "NOT_AVAILABLE", message: REWARDS_UNAVAILABLE_MESSAGE, action: "close" };
  const d = detailsOf(err);
  const fail = (message: string, action: RewardFailureAction, extra: Partial<RewardFailure> = {}): RewardFailure => ({ code: err.code, message, action, ...extra });
  switch (err.code) {
    case "DAILY_CAP_REACHED": {
      if (d.eligibility === "daily_limit_reached") return fail("This task reached its limit for today (UTC). Nothing was reserved. Try again tomorrow.", "close");
      const cap = num(d.capCoins) ?? ctx.dailyCapCoins ?? DEFAULT_DAILY_CAP_COINS;
      return fail(`You reached today's limit of ${formatCoins(cap)} from sponsored tasks. Nothing was reserved. Try again after midnight UTC.`, "close");
    }
    case "CLAIM_TOO_EARLY": {
      const claimableAt = str(d.claimableAt);
      if (d.reason === "link_not_opened") return fail("Open the sponsor's link from this task first, then come back to claim.", "reload_attempt", { claimableAt });
      if (d.reason === "waiting_for_partner") return fail("The sponsor has not confirmed the outcome yet. Townsy keeps checking; you can claim once it does.", "reload_attempt", { claimableAt });
      return fail("Not yet: you can claim when the countdown ends.", "reload_attempt", { claimableAt });
    }
    case "ATTEMPT_EXPIRED":
      return fail("This attempt has ended. Its reservation was released and nothing was charged. Start again if the task is still available.", "reload_attempt");
    case "BUDGET_EXHAUSTED":
      return fail("The sponsor's budget for this task is used up right now. Nothing was reserved.", "close");
    case "QUIZ_INCORRECT": {
      const left = num(d.triesLeft);
      if (left !== null && left > 0) return fail(`That is not the right answer. ${left} ${left === 1 ? "try" : "tries"} left.`, "answer_again", { triesLeft: left });
      return fail("That is not the right answer, and no tries are left. Nothing was charged; this quiz is now closed for you.", "reload_attempt", { triesLeft: 0 });
    }
    case "OFFER_NOT_ELIGIBLE": {
      const parsed = OfferEligibility.safeParse(d.eligibility);
      const e = parsed.success ? parsed.data : "unavailable";
      const copy = eligibilityCopy(e, { dailyCapCoins: ctx.dailyCapCoins });
      const action: RewardFailureAction = e === "sign_in_required" ? "sign_in" : e === "email_unverified" ? "verify_email" : e === "quiz_failed" ? "reload_attempt" : "close";
      return fail(copy.message ?? "This task is not available right now.", action, { eligibility: e });
    }
    case "ATTEMPT_IN_PROGRESS":
      return fail("You already have a sponsored task in progress. Finish or cancel it first (one task at a time).", "show_active", { attemptId: str(d.attemptId) });
    case "EMAIL_NOT_VERIFIED":
      return fail("Verify your email address before you earn coins (see your Account page).", "verify_email");
    case "UNAUTHENTICATED":
      return fail("Create an account or sign in to earn coins. Your session may have expired.", "sign_in");
    case "FORBIDDEN":
      return fail(ctx.stage === "start" ? "Create an account to earn coins. Guests can look at tasks but cannot collect coins." : "Your account can't do this right now.", ctx.stage === "start" ? "sign_in" : "close");
    case "BANNED":
      return fail("Your account can't earn coins right now.", "close");
    case "RATE_LIMITED":
      return fail("Too many requests in a short time. Wait a moment, then try again.", "retry");
    case "IDEMPOTENCY_CONFLICT":
      return fail("This request clashed with an earlier one. Nothing extra was awarded. Showing the task as Townsy sees it.", "reload_attempt");
    case "NOT_FOUND":
      return fail(ctx.stage === "load" || ctx.stage === "start" ? "This task is no longer available." : "This attempt was not found.", "close");
    case "FEATURE_DISABLED":
      return fail("Sponsored tasks are switched off right now.", "close");
    case "VALIDATION_FAILED":
      return fail(ctx.kind === "quiz" && ctx.stage === "claim" ? "Choose an answer first." : err.message || "The request was not accepted.", "close");
    case "DEPENDENCY_UNAVAILABLE":
    case "INTERNAL":
      return fail("Townsy couldn't confirm this step. Try again: a repeated request never awards or charges twice.", "retry");
    default:
      return fail(err.message || "The task could not be completed.", err.retryable ? "retry" : "close");
  }
}

/**
 * Advertiser and staff actions (campaign create / edit / submit / activate / pause, review decisions,
 * partner keys, house campaigns): the server's domain messages are specific and safe to show as they are;
 * only transport failures and missing routes get Townsy's own words.
 */
export function campaignErrorMessage(err: unknown, what = "this change"): string {
  if (!isApiError(err)) return "Couldn't reach Townsy. Check your connection and try again.";
  if (isRewardsUnavailable(err)) return REWARDS_UNAVAILABLE_MESSAGE;
  switch (err.code) {
    case "UNAUTHENTICATED":
      return "Sign in again to continue. Your session may have expired.";
    case "EMAIL_NOT_VERIFIED":
      return "Verify your email address first (see your Account page).";
    case "RATE_LIMITED":
      return err.message && !/^Too many requests/.test(err.message) ? err.message : "Too many changes in a short time. Wait a minute, then try again.";
    case "DEPENDENCY_UNAVAILABLE":
    case "INTERNAL":
      return `Townsy couldn't save ${what} right now. Try again in a moment.`;
    case "VALIDATION_FAILED": {
      const issues = detailsOf(err).issues;
      const first = Array.isArray(issues) ? (issues[0] as { path?: unknown; message?: unknown } | undefined) : undefined;
      const path = first && typeof first.path === "string" ? first.path : Array.isArray(first?.path) ? (first.path as unknown[]).join(".") : null;
      const message = first && typeof first.message === "string" ? first.message : null;
      if (message && message !== err.message) return `${err.message} ${path ? `(${path}: ${message})` : message}`;
      return err.message || "Some fields are not valid.";
    }
    default:
      return err.message || `Townsy couldn't save ${what}.`;
  }
}
